<template>
  <section class="capability-workbench">
    <div class="workbench-header">
      <div>
        <h3>模型目录</h3>
        <p>这里只决定用户能看到哪些模型。目录可以混合多个供应商；系统没人手动选时走哪个默认模型，仍然由上面的运行时配置控制。</p>
      </div>
      <el-tag type="info">只管可见目录</el-tag>
    </div>

    <div class="capability-grid">
      <article v-for="section in capabilitySections" :key="section.field" class="capability-card">
        <div class="capability-card__header">
          <div>
            <h4>{{ section.title }}</h4>
            <p>{{ section.description }}</p>
          </div>
          <div class="card-actions">
            <el-button text @click="syncSeed(section.field)">带入当前默认</el-button>
            <el-button text @click="openAddDialog(section.field)">新增</el-button>
          </div>
        </div>

        <div class="card-summary">
          <span>运行时默认供应商：{{ providerForField(section.field) || '未设置' }}</span>
          <span>已发布 {{ publishedItems(section.field).length }} 个</span>
          <span>{{ section.summaryHint }}</span>
        </div>

        <div class="card-hint">
          目录里可以同时放通义、火山等不同供应商模型；要真正可用，对应供应商的接入配置也需要在上方准备好。
        </div>

        <div v-if="publishedItems(section.field).length" class="published-strip">
          <div v-for="item in publishedItems(section.field)" :key="`${section.field}-published-${item.id}`" class="published-chip">
            <strong>{{ item.model }}</strong>
            <span class="published-chip__provider">{{ item.provider || '未设置供应商' }}</span>
            <span v-if="item.description">{{ item.description }}</span>
          </div>
        </div>

        <div v-if="!modelValue[section.field].length" class="lane-empty">
          {{ section.emptyPool }}
        </div>

        <div v-for="(item, index) in modelValue[section.field]" :key="item.id || `${section.field}-${index}`" class="model-row">
          <div class="model-row__main">
            <el-input :model-value="item.model" :placeholder="section.modelPlaceholder" @update:model-value="updateModel(section.field, index, $event)" />
            <el-select
              :model-value="item.provider || providerForField(section.field)"
              filterable
              allow-create
              default-first-option
              placeholder="选择供应商"
              @update:model-value="updateItem(section.field, index, { provider: String($event || '').trim() })"
            >
              <el-option v-for="option in providerOptionsForField(section.field)" :key="`${section.field}-${option.value}`" :label="option.label" :value="option.value" />
            </el-select>
          </div>

          <div class="model-row__meta">
            <span>供应商：{{ item.provider || providerForField(section.field) || '未设置' }}</span>
            <span v-if="item.description">说明：{{ item.description }}</span>
          </div>

          <div class="model-row__desc">
            <el-input :model-value="item.description || ''" placeholder="补充说明，可选" @update:model-value="updateItem(section.field, index, { description: $event })" />
          </div>

          <div v-if="section.supportsOperations" class="model-row__ops">
            <span>适用操作</span>
            <el-select
              :model-value="item.operations"
              multiple
              collapse-tags
              collapse-tags-tooltip
              placeholder="留空表示所有文本操作都可用"
              @update:model-value="updateItem(section.field, index, { operations: $event })"
            >
              <el-option v-for="option in textOperationOptions" :key="option.value" :label="option.label" :value="option.value" />
            </el-select>
          </div>

          <div class="model-row__actions">
            <el-checkbox :model-value="item.enabled" @update:model-value="setPublished(section.field, index, $event)">发布到目录</el-checkbox>
            <el-button text type="danger" @click="removeItem(section.field, index)">删除</el-button>
          </div>
        </div>
      </article>
    </div>

    <el-dialog
      v-model="addDialog.visible"
      :title="`新增${sectionTitle(addDialog.field)}`"
      width="520px"
      destroy-on-close
    >
      <div class="add-dialog">
        <div class="add-dialog__meta">
          <span>运行时默认供应商：{{ providerForField(addDialog.field) || '未设置' }}</span>
          <span>{{ sectionHint(addDialog.field) }}</span>
        </div>

        <el-form label-position="top">
          <el-form-item label="供应商">
            <el-select
              v-model="addDialog.provider"
              filterable
              allow-create
              default-first-option
              placeholder="选择供应商"
            >
              <el-option v-for="option in providerOptionsForField(addDialog.field)" :key="`add-${addDialog.field}-${option.value}`" :label="option.label" :value="option.value" />
            </el-select>
          </el-form-item>

          <el-form-item label="模型名">
            <el-input
              v-model="addDialog.model"
              :placeholder="sectionPlaceholder(addDialog.field)"
              clearable
            />
          </el-form-item>

          <el-form-item label="说明">
            <el-input
              v-model="addDialog.description"
              placeholder="可选，给管理员自己留备注"
              clearable
            />
          </el-form-item>

          <el-form-item v-if="fieldSupportsOperations(addDialog.field)" label="适用操作">
            <el-select
              v-model="addDialog.operations"
              multiple
              collapse-tags
              collapse-tags-tooltip
              placeholder="留空表示所有文本操作都可用"
            >
              <el-option v-for="option in textOperationOptions" :key="option.value" :label="option.label" :value="option.value" />
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-checkbox v-model="addDialog.enabled">发布到目录</el-checkbox>
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="add-dialog__footer">
          <el-button @click="closeAddDialog">取消</el-button>
          <el-button type="primary" :disabled="!addDialog.model.trim()" @click="confirmAddDialog">添加</el-button>
        </div>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { reactive } from 'vue'

import type { ModelCatalogConfig, ModelCatalogItem } from '@/api/config'

type ModelCatalogField = keyof ModelCatalogConfig

type CapabilitySection = {
  field: ModelCatalogField
  title: string
  description: string
  summaryHint: string
  modelPlaceholder: string
  emptyPool: string
  emptyPublished: string
  publishedTitle: string
  publishedHint: string
  supportsOperations: boolean
}

const props = defineProps<{
  modelValue: ModelCatalogConfig
  seedCatalog: ModelCatalogConfig
  textProvider: string
  imageProvider: string
  videoProvider: string
  complianceProvider: string
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: ModelCatalogConfig): void
}>()

const capabilitySections: CapabilitySection[] = [
  {
    field: 'text_models',
    title: '文本模型',
    description: '覆盖内容生成、脚本审批修订和提示词优化。',
    summaryHint: '用户下拉里只显示模型名，比如 qwen3.5-flash。',
    modelPlaceholder: '例如 qwen3.5-flash',
    emptyPool: '还没有可用文本模型，先同步当前默认或手动新增。',
    emptyPublished: '还没有发布到文本目录。',
    publishedTitle: '前台文本目录',
    publishedHint: '用户在需求生成和审批修订时会看到这里的模型。',
    supportsOperations: true,
  },
  {
    field: 'image_models',
    title: '图片模型',
    description: '用于分镜出图、角色图和背景图的模型池。',
    summaryHint: '这里只管用户能不能选到，不管系统默认走哪个。',
    modelPlaceholder: '例如 doubao-seedream-4-5-251128',
    emptyPool: '还没有可用图片模型。',
    emptyPublished: '还没有发布到图片目录。',
    publishedTitle: '前台图片目录',
    publishedHint: '分镜出图默认会从这里选择用户可见模型。',
    supportsOperations: false,
  },
  {
    field: 'video_models',
    title: '视频模型',
    description: '用于镜头视频生成与成片合成前的镜头级生产。',
    summaryHint: '用户只会看到模型名，不会看到后台默认策略。',
    modelPlaceholder: '例如 doubao-seedance-1-0-pro-fast-251015',
    emptyPool: '还没有可用视频模型。',
    emptyPublished: '还没有发布到视频目录。',
    publishedTitle: '前台视频目录',
    publishedHint: '分镜视频默认会从这里选择用户可见模型。',
    supportsOperations: false,
  },
  {
    field: 'compliance_models',
    title: '内容审核模型',
    description: '用于系统级内容风控与交付前合规检查。',
    summaryHint: '系统内部目录，不对普通用户暴露默认概念。',
    modelPlaceholder: '例如 spam,porn,politics',
    emptyPool: '还没有可用内容审核模型。',
    emptyPublished: '还没有发布到内容审核目录。',
    publishedTitle: '系统审核目录',
    publishedHint: '这里发布的是系统可调用的审核模型，不直接面向普通用户。',
    supportsOperations: false,
  },
]

const textOperationOptions = [
  { label: '内容初稿生成', value: 'content_seed_generate' },
  { label: '内容植入整合', value: 'content_embed' },
  { label: '提示词优化', value: 'prompt_assist' },
  { label: '提案生成', value: 'workflow_proposal_generate' },
  { label: '脚本生成', value: 'workflow_script_generate' },
  { label: '分镜生成', value: 'workflow_storyboard_generate' },
]

const providerOptionsByField: Record<ModelCatalogField, Array<{ label: string; value: string }>> = {
  text_models: [
    { label: '阿里通义', value: 'ali_tongyi' },
    { label: '火山引擎 / 方舟', value: 'volcengine' },
    { label: 'Azure OpenAI / GPT', value: 'azure' },
    { label: 'OpenAI', value: 'openai' },
    { label: 'Gemini', value: 'gemini' },
    { label: 'Dify', value: 'dify' },
  ],
  image_models: [
    { label: '火山引擎 / 方舟', value: 'volcengine' },
    { label: '阿里通义', value: 'ali_tongyi' },
    { label: 'fal', value: 'fal' },
    { label: '即梦', value: 'jimeng' },
    { label: '可灵', value: 'kling' },
    { label: 'Midjourney', value: 'midjourney' },
  ],
  video_models: [
    { label: '火山引擎 / 方舟', value: 'volcengine' },
    { label: '阿里通义 / Wan', value: 'ali_tongyi' },
    { label: '即梦', value: 'jimeng' },
    { label: '可灵', value: 'kling' },
    { label: 'Runway', value: 'runway' },
  ],
  compliance_models: [
    { label: '阿里云', value: 'aliyun' },
    { label: '百度', value: 'baidu' },
    { label: '腾讯', value: 'tencent' },
  ],
}

const addDialog = reactive({
  visible: false,
  field: 'text_models' as ModelCatalogField,
  provider: 'ali_tongyi',
  model: '',
  description: '',
  operations: [] as string[],
  enabled: true,
})

function providerForField(field: ModelCatalogField) {
  if (field === 'text_models') {
    return props.textProvider
  }
  if (field === 'image_models') {
    return props.imageProvider
  }
  if (field === 'video_models') {
    return props.videoProvider
  }
  return props.complianceProvider
}

function normalizeFieldItems(field: ModelCatalogField, items: ModelCatalogItem[]) {
  const normalized = items.map((item) => ({
    ...item,
    provider: String(item.provider || providerForField(field) || '').trim(),
    operations: field === 'text_models' ? [...(item.operations || [])] : [],
  }))

  const hasEnabledDefault = normalized.some((item) => item.enabled && item.is_default)
  let defaultAssigned = false
  return normalized.map((item) => {
    if (!item.enabled) {
      return { ...item, is_default: false }
    }
    if (!defaultAssigned && item.is_default) {
      defaultAssigned = true
      return item
    }
    if (!defaultAssigned && !hasEnabledDefault) {
      defaultAssigned = true
      return { ...item, is_default: true }
    }
    return { ...item, is_default: false }
  })
}

function emitCatalog(nextCatalog: ModelCatalogConfig) {
  emit('update:modelValue', nextCatalog)
}

function updateField(field: ModelCatalogField, items: ModelCatalogItem[]) {
  emitCatalog({
    ...props.modelValue,
    [field]: normalizeFieldItems(field, items),
  })
}

function updateItem(field: ModelCatalogField, index: number, patch: Partial<ModelCatalogItem>) {
  const nextItems = props.modelValue[field].map((item, itemIndex) => {
    if (itemIndex !== index) {
      return item
    }
    return {
      ...item,
      ...patch,
      provider: String(patch.provider ?? item.provider ?? providerForField(field) ?? '').trim(),
      operations: field === 'text_models' ? [...((patch.operations as string[] | undefined) ?? item.operations ?? [])] : [],
    }
  })
  updateField(field, nextItems)
}

function sectionConfig(field: ModelCatalogField) {
  return capabilitySections.find((section) => section.field === field) || capabilitySections[0]
}

function sectionTitle(field: ModelCatalogField) {
  return sectionConfig(field).title
}

function sectionPlaceholder(field: ModelCatalogField) {
  return sectionConfig(field).modelPlaceholder
}

function sectionHint(field: ModelCatalogField) {
  return sectionConfig(field).summaryHint
}

function fieldSupportsOperations(field: ModelCatalogField) {
  return sectionConfig(field).supportsOperations
}

function providerOptionsForField(field: ModelCatalogField) {
  return providerOptionsByField[field] || []
}

function updateModel(field: ModelCatalogField, index: number, value: unknown) {
  const model = String(value || '').trim()
  const currentItem = props.modelValue[field][index]
  const nextLabel = currentItem && (!String(currentItem.label || '').trim() || String(currentItem.label || '').trim() === String(currentItem.model || '').trim())
    ? model
    : currentItem?.label
  updateItem(field, index, {
    model,
    label: nextLabel,
  })
}

function setPublished(field: ModelCatalogField, index: number, checked: unknown) {
  const nextItems = props.modelValue[field].map((item, itemIndex) => (
    itemIndex === index
      ? { ...item, enabled: Boolean(checked), is_default: Boolean(checked) ? item.is_default : false }
      : item
  ))
  updateField(field, nextItems)
}

function addItem(field: ModelCatalogField, payload?: { model: string; description: string; operations: string[]; enabled: boolean }) {
  updateField(field, [
    ...props.modelValue[field],
    {
      id: `${field}-${Date.now()}`,
      label: String(payload?.model || '').trim(),
      model: String(payload?.model || '').trim(),
      provider: String((payload as any)?.provider || providerForField(field) || '').trim(),
      description: String(payload?.description || '').trim(),
      capability: '',
      enabled: payload?.enabled ?? true,
      is_default: !props.modelValue[field].some((item) => item.enabled && item.is_default),
      operations: field === 'text_models' ? [...(payload?.operations || [])] : [],
    },
  ])
}

function openAddDialog(field: ModelCatalogField) {
  addDialog.visible = true
  addDialog.field = field
  addDialog.provider = providerForField(field)
  addDialog.model = ''
  addDialog.description = ''
  addDialog.operations = []
  addDialog.enabled = true
}

function closeAddDialog() {
  addDialog.visible = false
}

function confirmAddDialog() {
  const model = addDialog.model.trim()
  if (!model) {
    return
  }
  addItem(addDialog.field, {
    model,
    provider: addDialog.provider.trim(),
    description: addDialog.description.trim(),
    operations: fieldSupportsOperations(addDialog.field) ? [...addDialog.operations] : [],
    enabled: addDialog.enabled,
  } as { model: string; provider: string; description: string; operations: string[]; enabled: boolean })
  closeAddDialog()
}

function removeItem(field: ModelCatalogField, index: number) {
  updateField(field, props.modelValue[field].filter((_, itemIndex) => itemIndex !== index))
}

function mergeSeedItems(field: ModelCatalogField, existingItems: ModelCatalogItem[], seedItems: ModelCatalogItem[]) {
  const nextItems = existingItems.map((item) => ({
    ...item,
    operations: field === 'text_models' ? [...(item.operations || [])] : [],
  }))

  seedItems.forEach((seed) => {
    if (!String(seed.model || '').trim()) {
      return
    }
    const matchIndex = nextItems.findIndex((item) => (
      item.id === seed.id || (
        String(item.provider || '').trim() === String(seed.provider || '').trim() &&
        String(item.model || '').trim() === String(seed.model || '').trim()
      )
    ))
    if (matchIndex >= 0) {
      const current = nextItems[matchIndex]
      nextItems[matchIndex] = {
        ...current,
        ...seed,
        enabled: current.enabled,
        is_default: current.is_default,
        operations: field === 'text_models'
          ? (current.operations?.length ? [...current.operations] : [...(seed.operations || [])])
          : [],
      }
      return
    }
    nextItems.push({
      ...seed,
      operations: field === 'text_models' ? [...(seed.operations || [])] : [],
    })
  })

  return normalizeFieldItems(field, nextItems)
}

function syncSeed(field: ModelCatalogField) {
  updateField(field, mergeSeedItems(field, props.modelValue[field], props.seedCatalog[field]))
}

function publishedItems(field: ModelCatalogField) {
  return props.modelValue[field].filter((item) => item.enabled)
}

function formatOperationLabels(values: string[]) {
  return values.map((value) => textOperationOptions.find((option) => option.value === value)?.label || value).join(' / ')
}
</script>

<style scoped>
.capability-workbench {
  display: grid;
  gap: 18px;
}

.workbench-header,
.capability-card__header,
.model-row__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.workbench-header h3,
.capability-card__header h4,
.published-chip strong {
  margin: 0;
}

.workbench-header p,
.capability-card__header p,
.lane-empty {
  margin: 6px 0 0;
  color: #64748b;
}

.capability-grid {
  display: grid;
  gap: 16px;
}

.capability-card {
  padding: 20px;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.card-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 14px;
  color: #475569;
  font-size: 13px;
}

.card-hint {
  margin-top: 12px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.published-strip {
  display: grid;
  gap: 10px;
  margin-top: 14px;
  padding: 14px;
  border-radius: 14px;
  background: linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
}

.published-chip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.published-chip span {
  color: #64748b;
  font-size: 13px;
}

.published-chip__provider {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.72);
  color: #475569;
}

.model-row {
  display: grid;
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid #e2e8f0;
}

.model-row:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.model-row__main {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.model-row__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  color: #64748b;
  font-size: 13px;
}

.model-row__desc {
  display: grid;
}

.model-row__ops {
  display: grid;
  gap: 8px;
}

.model-row__ops span {
  color: #475569;
  font-size: 13px;
}

.add-dialog {
  display: grid;
  gap: 12px;
}

.add-dialog__meta,
.add-dialog__footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.add-dialog__meta {
  color: #64748b;
  font-size: 13px;
}

.lane-empty {
  padding: 18px 0 0;
  font-size: 13px;
}

@media (max-width: 1080px) {
  .model-row__main {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .workbench-header,
  .capability-card__header,
  .model-row__actions,
  .add-dialog__footer {
    display: grid;
    grid-template-columns: 1fr;
  }

  .card-actions {
    justify-content: flex-start;
  }
}
</style>